// Local highlight colours for Newsquawk-owned combined watchlists.
//
// The same arrangement prebuiltWatchlists.ts has for watchlists: a desk list
// is read-only on the server, so a user's own colour for it lives in
// localStorage and is layered on at the useHeadlineQueriesCRUD layer. Kept in a
// separate key because combined watchlist ids and portfolio ids are separate
// sequences — one map would let "portfolio 7" recolour "combined watchlist 7".

import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";

const OVERRIDES_KEY = "prebuiltCombinedWatchlistColors";

export type PrebuiltCombinedColorMap = Record<number, string>;

const readRaw = (): PrebuiltCombinedColorMap => {
  try {
    const raw = localStorage.getItem(OVERRIDES_KEY);
    return raw ? (JSON.parse(raw) as PrebuiltCombinedColorMap) : {};
  } catch {
    return {};
  }
};

const writeRaw = (overrides: PrebuiltCombinedColorMap): void => {
  try {
    localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides));
  } catch {
    // ignore quota / access errors
  }
};

// Cached snapshot for useSyncExternalStore — see prebuiltWatchlists.ts for why
// the identity must only change on a write.
const listeners = new Set<() => void>();
let cachedSnapshot: PrebuiltCombinedColorMap | null = null;
const emit = (): void => {
  cachedSnapshot = null;
  listeners.forEach((l) => l());
};

export const subscribeToPrebuiltCombinedColors = (
  cb: () => void
): (() => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

export const getPrebuiltCombinedColorsSnapshot = (): PrebuiltCombinedColorMap => {
  if (cachedSnapshot === null) cachedSnapshot = readRaw();
  return cachedSnapshot;
};

export const setPrebuiltCombinedColor = (id: number, color: string): void => {
  const overrides = readRaw();
  overrides[id] = color;
  writeRaw(overrides);
  emit();
};

export const clearPrebuiltCombinedColor = (id: number): void => {
  const overrides = readRaw();
  if (!(id in overrides)) return;
  delete overrides[id];
  writeRaw(overrides);
  emit();
};

/** Layers local colours onto Newsquawk-owned combined watchlists. USER ones pass through. */
export const applyPrebuiltCombinedColors = (
  queries: CategoryQuery[],
  overrides: PrebuiltCombinedColorMap
): CategoryQuery[] => {
  if (Object.keys(overrides).length === 0) return queries;
  return queries.map((q) => {
    if (q.owner_type !== "NEWSQUAWK" || q.id === undefined) return q;
    const color = overrides[q.id];
    return color ? { ...q, color } : q;
  });
};
